import {
    useHasBillCounterpartiesInWorkspace,
    useHasBillsInWorkspace,
} from "@api/bill.api";
import {
    RenderableTableEmptyViewVariant,
    useTableEmptyView,
} from "@design-system/tableEmptyView/useTableEmptyView";
import { useCallback } from "react";
import { BillTableEmptyView, BillTableViewKind } from "./BillTableEmptyView";

interface UseBillTableEmptyViewParams {
    isLoading: boolean;
    filteredCount: number;
    hasConnectedBillAccounts: boolean;
    onClearFilters: () => void;
    viewKind: BillTableViewKind;
}

export function useBillTableEmptyView({
    isLoading,
    filteredCount,
    hasConnectedBillAccounts,
    onClearFilters,
    viewKind,
}: UseBillTableEmptyViewParams) {
    const isEmpty = !isLoading && filteredCount === 0;
    const isCounterpartyView = viewKind === "vendors";

    const billsProbe = useHasBillsInWorkspace(isEmpty && !isCounterpartyView);
    const counterpartiesProbe = useHasBillCounterpartiesInWorkspace(
        isEmpty && isCounterpartyView,
    );
    const { hasRecords, isChecking } = isCounterpartyView
        ? counterpartiesProbe
        : billsProbe;

    const renderEmptyView = useCallback(
        (variant: RenderableTableEmptyViewVariant) => (
            <BillTableEmptyView
                variant={variant}
                viewKind={viewKind}
                onClearFilters={onClearFilters}
            />
        ),
        [onClearFilters, viewKind],
    );

    const { emptyView, shouldHideFooter } = useTableEmptyView({
        isEmpty,
        hasConnectedAccounts: hasConnectedBillAccounts,
        hasRecordsInWorkspace: hasRecords,
        isCheckingWorkspaceRecords: isChecking,
        renderEmptyView,
    });

    return { emptyView, shouldHideFooter };
}
